/**
 * Emoji-Auswahl (wiederverwendbar): Unicode-Emojis aus `emojibase-data` (deutsche Namen, englische Kurzcodes,
 * per dynamischem Import nachgeladen), eigene Emojis aller Server inkl. animierter, Suche, Kategorien und
 * „Zuletzt benutzt“ (localStorage). Shift+Klick wählt aus, ohne zu schließen.
 */
import type { EmojiDto } from '@lagerfeuer/shared';
import { Clock, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppStore } from '../store/app';
import { Popover, type Anchor } from './Popover';
import { initials } from './Avatar';
import '../features/messages/messages.css';

/** Ausgewähltes Emoji: Unicode (`id = null`, `name` = Zeichen) oder eigenes Emoji. */
export interface PickedEmoji {
  id: string | null;
  name: string;
  animated: boolean;
  /** Nur eigene Emojis: Bild-URL. */
  url?: string | null;
}

export interface UnicodeEmoji {
  unicode: string;
  label: string;
  group: number;
  order: number;
  tags: string[];
  shortcodes: string[];
}

export interface EmojiGroup {
  id: number;
  label: string;
}

interface EmojiData {
  emojis: UnicodeEmoji[];
  groups: EmojiGroup[];
}

interface RawEmoji {
  unicode: string;
  label: string;
  group?: number;
  order?: number;
  tags?: string[];
  hexcode: string;
}

let dataPromise: Promise<EmojiData> | null = null;
let loaded: EmojiData | null = null;

/** Lädt die Unicode-Emoji-Daten einmalig (eigener Chunk). */
// eslint-disable-next-line react-refresh/only-export-components
export function loadUnicodeEmojis(): Promise<EmojiData> {
  dataPromise ??= Promise.all([
    import('emojibase-data/de/compact.json'),
    import('emojibase-data/de/messages.json'),
    import('emojibase-data/en/shortcodes/github.json'),
  ])
    .then(([compact, messages, shortcodes]) => {
      const raw = (compact.default ?? compact) as unknown as RawEmoji[];
      const msgs = (messages.default ?? messages) as unknown as { groups: { key: string; message: string; order: number }[] };
      const codes = (shortcodes.default ?? shortcodes) as unknown as Record<string, string | string[]>;
      const emojis: UnicodeEmoji[] = [];
      for (const e of raw) {
        if (e.group === undefined || e.group === 2) continue; // Komponenten (Hauttöne o. Ä.) und Regional-Indikatoren
        const sc = codes[e.hexcode];
        emojis.push({
          unicode: e.unicode,
          label: e.label,
          group: e.group,
          order: e.order ?? 0,
          tags: e.tags ?? [],
          shortcodes: sc ? (Array.isArray(sc) ? sc : [sc]) : [],
        });
      }
      emojis.sort((a, b) => a.order - b.order);
      const groups = msgs.groups.filter((g) => g.order !== 2).map((g) => ({ id: g.order, label: g.message }));
      loaded = { emojis, groups };
      return loaded;
    })
    .catch((e: unknown) => {
      dataPromise = null;
      throw e;
    });
  return dataPromise;
}

/** Suche in Unicode-Emojis (Kurzcode, Name, Schlagwörter). Liefert [] solange die Daten nicht geladen sind. */
// eslint-disable-next-line react-refresh/only-export-components
export function searchUnicodeEmojis(query: string, limit = 50): UnicodeEmoji[] {
  if (!loaded) return [];
  const q = query.toLowerCase();
  if (!q) return loaded.emojis.slice(0, limit);
  const scored: { e: UnicodeEmoji; s: number }[] = [];
  for (const e of loaded.emojis) {
    let s = 0;
    if (e.shortcodes.some((c) => c === q)) s = 100;
    else if (e.shortcodes.some((c) => c.startsWith(q))) s = 80;
    else if (e.label.toLowerCase().startsWith(q)) s = 60;
    else if (e.shortcodes.some((c) => c.includes(q))) s = 40;
    else if (e.tags.some((t) => t.startsWith(q))) s = 30;
    else if (e.label.toLowerCase().includes(q)) s = 20;
    if (s) scored.push({ e, s });
  }
  scored.sort((a, b) => b.s - a.s || a.e.order - b.e.order);
  return scored.slice(0, limit).map((x) => x.e);
}

// ---------- Zuletzt benutzt ----------

const RECENT_KEY = 'lf:emoji-recent';
const RECENT_MAX = 27;

// eslint-disable-next-line react-refresh/only-export-components
export function recentEmojis(): PickedEmoji[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as unknown;
    if (!Array.isArray(raw)) return [];
    return raw.filter((x): x is PickedEmoji => !!x && typeof x === 'object' && typeof (x as PickedEmoji).name === 'string').slice(0, RECENT_MAX);
  } catch {
    return [];
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export function pushRecentEmoji(e: PickedEmoji): void {
  try {
    const key = (x: PickedEmoji) => x.id ?? x.name;
    const list = [{ id: e.id, name: e.name, animated: e.animated }, ...recentEmojis().filter((x) => key(x) !== key(e))].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    // localStorage nicht verfügbar (privater Modus) – ignorieren.
  }
}

// ---------- Komponente ----------

export interface EmojiPickerProps {
  onSelect(e: PickedEmoji, opts: { keepOpen: boolean }): void;
  /** Aktueller Server (dessen Emojis stehen vorne; fremde nur mit `allowExternal`). */
  serverId?: string | null;
  allowExternal?: boolean;
  /** Nur Unicode-Emojis anbieten (z. B. Rollen-Emoji). */
  unicodeOnly?: boolean;
  /** Überschrift, z. B. „Super-Reaktion“. */
  title?: string;
  testId?: string;
}

interface Section {
  key: string;
  label: string;
  icon: React.ReactNode;
  items: PickedEmoji[];
  /** Nur Unicode: Name für die Vorschau. */
  labels?: Map<string, string>;
}

const GROUP_ICONS = ['😀', '👋', '', '🐻', '🍔', '✈️', '⚽', '💡', '❤️', '🏁'];

function customToPicked(e: EmojiDto): PickedEmoji {
  return { id: e.id, name: e.name, animated: e.animated, url: e.url };
}

export function EmojiPicker({ onSelect, serverId, allowExternal = true, unicodeOnly = false, title, testId = 'emoji-picker' }: EmojiPickerProps) {
  const [data, setData] = useState<EmojiData | null>(loaded);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState('');
  const [hover, setHover] = useState<{ e: PickedEmoji; label: string } | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const servers = useAppStore((s) => s.servers);
  const serverOrder = useAppStore((s) => s.serverOrder);
  const emojis = useAppStore((s) => s.emojis);
  const [recent] = useState(recentEmojis);

  useEffect(() => {
    if (data) return;
    let alive = true;
    loadUnicodeEmojis()
      .then((d) => alive && setData(d))
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, [data]);

  const customSections = useMemo<Section[]>(() => {
    if (unicodeOnly) return [];
    const order = serverId ? [serverId, ...serverOrder.filter((id) => id !== serverId)] : serverOrder;
    const out: Section[] = [];
    for (const sid of order) {
      if (!allowExternal && sid !== serverId) continue;
      const list = (emojis[sid] ?? []).filter((e) => e.available);
      if (!list.length) continue;
      const server = servers[sid];
      out.push({
        key: `server-${sid}`,
        label: server?.name ?? 'Server',
        icon: server?.iconUrl ? <img src={server.iconUrl} alt="" /> : <span className="emoji-cat-initials">{initials(server?.name ?? '?').slice(0, 2)}</span>,
        items: list.map(customToPicked),
      });
    }
    return out;
  }, [emojis, serverOrder, servers, serverId, allowExternal, unicodeOnly]);

  const knownCustom = useMemo(() => new Set(customSections.flatMap((s) => s.items.map((i) => i.id))), [customSections]);

  const sections = useMemo<Section[]>(() => {
    const q = query.trim().toLowerCase().replace(/^:|:$/g, '');
    if (q) {
      const custom = customSections.flatMap((s) => s.items).filter((e) => e.name.toLowerCase().includes(q));
      const uni = data ? searchUnicodeEmojis(q, 150) : [];
      return [
        {
          key: 'search',
          label: 'Suchergebnisse',
          icon: <Search size={16} />,
          items: [...custom, ...uni.map((u) => ({ id: null, name: u.unicode, animated: false }))],
          labels: new Map(uni.map((u) => [u.unicode, u.shortcodes[0] ?? u.label])),
        },
      ];
    }
    const out: Section[] = [];
    const usableRecent = recent.filter((r) => !r.id || knownCustom.has(r.id)).map((r) => (r.id ? (customSections.flatMap((s) => s.items).find((x) => x.id === r.id) ?? r) : r));
    if (usableRecent.length) out.push({ key: 'recent', label: 'Zuletzt benutzt', icon: <Clock size={16} />, items: usableRecent });
    out.push(...customSections);
    if (data) {
      for (const g of data.groups) {
        const list = data.emojis.filter((e) => e.group === g.id);
        if (!list.length) continue;
        out.push({
          key: `group-${g.id}`,
          label: g.label,
          icon: <span aria-hidden="true">{GROUP_ICONS[g.id] || list[0]!.unicode}</span>,
          items: list.map((e) => ({ id: null, name: e.unicode, animated: false })),
          labels: new Map(list.map((e) => [e.unicode, e.shortcodes[0] ?? e.label])),
        });
      }
    }
    return out;
  }, [query, customSections, data, recent, knownCustom]);

  const pick = (e: PickedEmoji, keepOpen: boolean) => {
    pushRecentEmoji(e);
    onSelect(e, { keepOpen });
  };

  const scrollTo = (key: string) => {
    const el = bodyRef.current?.querySelector<HTMLElement>(`[data-section="${key}"]`);
    if (el && bodyRef.current) bodyRef.current.scrollTop = el.offsetTop - bodyRef.current.offsetTop;
  };

  const first = sections[0]?.items[0];
  return (
    <div className="emoji-picker" data-testid={testId}>
      {title && <div className="emoji-picker-title">{title}</div>}
      <div className="emoji-picker-search">
        <Search size={16} aria-hidden="true" />
        <input
          className="input"
          autoFocus
          placeholder="Emoji suchen"
          aria-label="Emoji suchen"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && first) {
              e.preventDefault();
              pick(first, e.shiftKey);
            }
          }}
          data-testid="emoji-search"
        />
      </div>
      <div className="emoji-picker-main">
        {!query && (
          <nav className="emoji-picker-cats" aria-label="Kategorien">
            {sections.map((s) => (
              <button key={s.key} type="button" className="emoji-cat" title={s.label} aria-label={s.label} onClick={() => scrollTo(s.key)} data-testid={`emoji-category-${s.key}`}>
                {s.icon}
              </button>
            ))}
          </nav>
        )}
        <div className="emoji-picker-body scroll" ref={bodyRef} role="listbox" aria-label="Emojis">
          {sections.map((s) => (
            <section key={s.key} data-section={s.key} className="emoji-section">
              <h4 className="emoji-section-title">{s.label}</h4>
              <div className="emoji-grid">
                {s.items.map((e) => {
                  const label = e.id ? `:${e.name}:` : (s.labels?.get(e.name) ?? e.name);
                  return (
                    <button
                      key={`${s.key}-${e.id ?? e.name}`}
                      type="button"
                      role="option"
                      aria-selected={false}
                      className="emoji-option"
                      aria-label={label}
                      title={label}
                      data-testid="emoji-option"
                      data-emoji-name={e.id ? e.name : (s.labels?.get(e.name) ?? e.name)}
                      data-emoji-id={e.id ?? undefined}
                      onMouseEnter={() => setHover({ e, label })}
                      onFocus={() => setHover({ e, label })}
                      onClick={(ev) => pick(e, ev.shiftKey)}
                    >
                      <EmojiGlyph emoji={e} size={28} />
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
          {query && sections[0]?.items.length === 0 && <div className="empty small">Keine Emojis gefunden.</div>}
          {!data && !error && !query && <div className="empty small"><div className="spinner" /></div>}
          {error && <div className="empty small">Emojis konnten nicht geladen werden.</div>}
        </div>
      </div>
      <div className="emoji-picker-footer" aria-live="polite">
        {hover ? (
          <>
            <EmojiGlyph emoji={hover.e} size={24} />
            <span className="truncate">{hover.e.id ? `:${hover.e.name}:` : `:${hover.label}:`}</span>
          </>
        ) : (
          <span className="faint small">Shift+Klick wählt mehrere aus</span>
        )}
      </div>
    </div>
  );
}

/** Ein Emoji als Bild (eigenes) oder Zeichen (Unicode). */
export function EmojiGlyph({ emoji, size = 20 }: { emoji: PickedEmoji; size?: number }) {
  const autoplay = useAppStore((s) => s.settings?.autoplayGifs ?? true);
  if (emoji.id) {
    if (!emoji.url) return <span className="emoji-text">:{emoji.name}:</span>;
    return (
      <img
        className={`emoji ${emoji.animated ? 'emoji-animated' : ''} ${emoji.animated && !autoplay ? 'emoji-paused' : ''}`}
        src={emoji.url}
        alt={`:${emoji.name}:`}
        width={size}
        height={size}
        loading="lazy"
        draggable={false}
      />
    );
  }
  return (
    <span className="emoji-unicode" style={{ fontSize: size * 0.85, lineHeight: `${size}px` }} aria-hidden="true">
      {emoji.name}
    </span>
  );
}

/** Picker als Popover an einem Anker. */
export function EmojiPickerPopover({
  anchor,
  onClose,
  placement = 'bottom-start',
  ...props
}: EmojiPickerProps & { anchor: Anchor; onClose: () => void; placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end' | 'left-start' | 'right-start' }) {
  return (
    <Popover anchor={anchor} onClose={onClose} placement={placement} testId="emoji-picker-popover" label={props.title ?? 'Emoji auswählen'} className="emoji-picker-popover">
      <EmojiPicker
        {...props}
        onSelect={(e, opts) => {
          props.onSelect(e, opts);
          if (!opts.keepOpen) onClose();
        }}
      />
    </Popover>
  );
}
